import {
  ChartConfiguration,
  chartConfiguration,
} from '@models/schema/chartConfiguration';
import { z } from 'zod';

const locationData = z.object({
  location: z.string(),
  visitIndex: z.number(),
});

const dmaLocationData = locationData.merge(z.object({ dmaId: z.number() }));

const brandLocationData = z.object({
  name: z.string(),
  city: z.string(),
  address: z.string(),
  state: z.string(),
  zip: z.string(),
  index: z.number(),
  locationId: z.string().nullable(),
  locationListId: z.number().nullable(),
  locationListExclusionId: z.number().nullable(),
});

const timeToFirstVisitsByDayData = z.object({
  daysAfterAdExposure: z.number(),
  percentageOfUsers: z.number(),
});

const visitsByDayOfWeekData = z.object({
  monday: z.number(),
  tuesday: z.number(),
  wednesday: z.number(),
  thursday: z.number(),
  friday: z.number(),
  saturday: z.number(),
  sunday: z.number(),
});

const visitsByHourOfDayData = z.object({
  hour: z.number(),
  visitIndex: z.number(),
  visits: z.number(),
});

const nameValuePair = z.object({
  name: z.string(),
  value: z.number(),
});

const nameValueGroup = nameValuePair.extend({
  group: z.string().nullable(),
});

const nameValuePairWithSortIndex = nameValuePair.merge(
  z.object({
    sortIndex: z.number(),
  })
);

const demographicProfileData = z.object({
  statsByGender: z.array(nameValuePairWithSortIndex),
  statsByIncome: z.array(nameValuePairWithSortIndex),
  statsByAge: z.array(nameValuePairWithSortIndex),
  statsByEducation: z.array(nameValuePairWithSortIndex),
  statsByHomeownership: z.array(nameValuePairWithSortIndex),
  statsByMaritalStatus: z.array(nameValuePairWithSortIndex),
  statsByChildrenPresence: z.array(nameValuePairWithSortIndex),
  statsByPets: z.array(nameValuePairWithSortIndex),
});

export const campaignAnalyticsReportsResponse = z.array(
  z.object({
    id: z.number(),
    reportDate: z.string(),
    reportType: z.enum(['weekly', 'custom']),
    status: z.enum(['approved', 'pending']),
    lastUpdated: z.string().nullable(),
    audienceProfile: z.intersection(
      chartConfiguration,
      z.object({
        data: z.object({
          visited: z.array(nameValueGroup),
        }),
      })
    ),
    demographicProfile: z.intersection(
      chartConfiguration,
      z.object({
        data: z.object({
          visited: demographicProfileData,
        }),
      })
    ),
    stateVisits: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(locationData),
      })
    ),
    dmaVisits: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(dmaLocationData),
      })
    ),
    brandLocationVisits: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(brandLocationData),
      })
    ),
    proximityToHome: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(nameValuePairWithSortIndex),
      })
    ),
    timeToFirstVisitByDay: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(timeToFirstVisitsByDayData),
      })
    ),
    visitsByDayOfWeek: z.intersection(
      chartConfiguration,
      z.object({
        data: visitsByDayOfWeekData,
      })
    ),
    visitsByHourOfDay: z.intersection(
      chartConfiguration,
      z.object({
        data: z.array(visitsByHourOfDayData),
      })
    ),
  })
);

export type CampaignAnalyticsReportsResponse = z.infer<
  typeof campaignAnalyticsReportsResponse
>;
export type CampaignAnalyticsReport = CampaignAnalyticsReportsResponse[number];
export type ChartTypeName = keyof Omit<
  CampaignAnalyticsReport,
  'id' | 'reportDate' | 'reportType' | 'status' | 'lastUpdated'
>;

export type LocationData = z.infer<typeof locationData>;
export type DmaLocationData = z.infer<typeof dmaLocationData>;
export type BrandLocationData = z.infer<typeof brandLocationData>;
export type ProximityToHomeData = z.infer<typeof nameValuePair>;
export type TimeToFirstVisitByDayData = z.infer<
  typeof timeToFirstVisitsByDayData
>;
export type VisitsByDayOfWeekData = z.infer<typeof visitsByDayOfWeekData>;
export type VisitsByHourOfDayData = z.infer<typeof visitsByHourOfDayData>;

export type UpdateRequest = Partial<
  Record<ChartTypeName, ChartConfiguration> &
    Pick<CampaignAnalyticsReport, 'status'>
>;

export type NameValuePair = z.infer<typeof nameValuePair>;

export type NameValuePairWithSortIndex = z.infer<
  typeof nameValuePairWithSortIndex
>;

const _locationListExclusionsSchema = z.array(
  z.object({
    id: z.number(),
    locationId: z.string(),
    locationListId: z.number(),
  })
);

export type LocationListExclusion = z.infer<
  typeof _locationListExclusionsSchema
>[0];
